/**
 * M-90 追補: 興味タグ設定モーダルの deep link（`/?interestConfig=1`）における
 * 「元の画面へ戻す」と「開いた痕跡を URL から消す」の判断を担う純関数群。
 *
 * 背景 — モーダルの持ち主は `CustomersListPage` 1 箇所に固定してある（2 画面に生やすと
 * 保存後の invalidate 対象が割れるため / `CustomersListPage.tsx` のコメント参照）。
 * その結果、販促プラン画面から開くと**顧客一覧へ遷移したまま戻れない**のと、
 * `?interestConfig=1` が残るせいで**閉じた後のリロードでモーダルが復活する**という
 * 2 つの副作用が出ていた。どちらもこのモジュールの判断で閉じる。
 */

/** deep link に載せる query key。呼び出し側と読み取り側で同じ定数を使う。 */
export const CONFIG_MODAL_PARAM = 'interestConfig';
export const RETURN_TO_PARAM = 'returnTo';

/**
 * `returnTo` として受け入れてよい**同一オリジン内のパス**か。
 *
 * URL は誰でも組み立てて共有できるので、ここを緩めると「管理画面のリンクを踏んだら
 * 外部サイトへ飛ぶ」オープンリダイレクトになる。react-router の `navigate()` は
 * 文字列をパスとして扱うため実際に別オリジンへ出るのは難しいが、**防御は入口で閉じる**。
 *
 * - `/` 始まりのみ許可（`https://…` / `javascript:` / 相対パスを排除）
 * - `//evil.com` は protocol-relative URL として解決されうるため排除
 * - `\` はブラウザによって `/` に正規化されるため `/\evil.com` 経由の迂回も排除
 */
export function isSafeInternalPath(path: string | null | undefined): path is string {
  if (!path) return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  if (path.includes('\\')) return false;
  return true;
}

/** deep link の URL を組み立てる（戻り先つき）。 */
export function buildInterestConfigLink(returnTo: string): string {
  const params = new URLSearchParams({ [CONFIG_MODAL_PARAM]: '1' });
  if (isSafeInternalPath(returnTo)) params.set(RETURN_TO_PARAM, returnTo);
  return `/?${params.toString()}`;
}

/** モーダルを閉じたときに何をするか。 */
export type ConfigModalCloseAction =
  /** `returnTo` へ戻る（プラン画面などから開かれた場合）。 */
  | { readonly type: 'navigate'; readonly to: string }
  /** その場に留まり、deep link の query だけ URL から落とす。 */
  | { readonly type: 'clearParams'; readonly search: string }
  /** URL を触らない（顧客一覧内のボタンから開いた場合 = deep link の query が最初から無い）。 */
  | { readonly type: 'none' };

/**
 * 閉じる操作の宛先を決める。
 *
 * `returnTo` が無い / 安全でない場合は**遷移せず**に `interestConfig` と `returnTo` だけを
 * 落とした search 文字列を返す（顧客一覧を直接開いてモーダルを出した人を、
 * 押していない画面へ飛ばさないため）。
 */
export function resolveConfigModalClose(
  search: URLSearchParams | string,
): ConfigModalCloseAction {
  const params = new URLSearchParams(search);
  const back = params.get(RETURN_TO_PARAM);
  if (isSafeInternalPath(back)) return { type: 'navigate', to: back };
  const hadParams = params.has(CONFIG_MODAL_PARAM) || params.has(RETURN_TO_PARAM);
  // 顧客一覧内のボタン（`CustomerAnalytics` の「興味タグ設定」）から開いた場合は
  // deep link の query がそもそも無い。ここで URL を書き換えると同一 URL への
  // replace 遷移が空振りするだけなので何もしない。
  if (!hadParams) return { type: 'none' };
  params.delete(CONFIG_MODAL_PARAM);
  params.delete(RETURN_TO_PARAM);
  return { type: 'clearParams', search: params.toString() };
}

/** マウント時にモーダルを開くべきか（deep link 判定）。 */
export function shouldOpenConfigModal(search: URLSearchParams | string): boolean {
  return new URLSearchParams(search).get(CONFIG_MODAL_PARAM) === '1';
}
